import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './use-auth';
import { toast } from './use-toast';

export interface Expense {
  id: string;
  company_id: string;
  category_id: string;
  vehicle_id: string | null;
  driver_id: string | null;
  description: string;
  value: number;
  due_date: string;
  paid_at: string | null;
  status: 'pending' | 'overdue' | 'paid';
  payment_type: 'single' | 'installment' | 'recurring';
  installment_number: number | null;
  installment_total: number | null;
  recurrence_interval: 'monthly' | 'quarterly' | 'yearly' | null;
  parent_expense_id: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
  fleet_expense_categories?: { name: string; icon: string; color: string };
  fleet_vehicles?: { plate: string; model: string };
  fleet_drivers?: { name: string };
}

export interface ExpenseFilters {
  status?: 'pending' | 'overdue' | 'paid' | 'all';
  categoryId?: string;
  vehicleId?: string;
  driverId?: string;
  search?: string;
}

export function useExpenses(filters?: ExpenseFilters) {
  const { activeCompany } = useAuth();

  return useQuery({
    queryKey: ['expenses', activeCompany?.id, filters],
    queryFn: async () => {
      if (!activeCompany?.id) throw new Error('No active company');

      let query = supabase
        .from('fleet_expenses')
        .select(`
          *,
          fleet_expense_categories(name, icon, color),
          fleet_vehicles(plate, model),
          fleet_drivers(name)
        `)
        .eq('company_id', activeCompany.id)
        .order('due_date', { ascending: false });

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.categoryId) {
        query = query.eq('category_id', filters.categoryId);
      }

      if (filters?.vehicleId) {
        query = query.eq('vehicle_id', filters.vehicleId);
      }

      if (filters?.driverId) {
        query = query.eq('driver_id', filters.driverId);
      }

      if (filters?.search) {
        query = query.or(`description.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Expense[];
    },
    enabled: !!activeCompany?.id,
  });
}

export function useExpenseSummary() {
  const { activeCompany } = useAuth();

  return useQuery({
    queryKey: ['expense-summary', activeCompany?.id],
    queryFn: async () => {
      if (!activeCompany?.id) throw new Error('No active company');

      const { data, error } = await supabase
        .from('fleet_expenses')
        .select('status, value')
        .eq('company_id', activeCompany.id);

      if (error) throw error;

      const summary = {
        overdue: { total: 0, count: 0 },
        pending: { total: 0, count: 0 },
        paid: { total: 0, count: 0 },
      };

      data.forEach((expense) => {
        const value = Number(expense.value);
        if (expense.status === 'overdue') {
          summary.overdue.total += value;
          summary.overdue.count += 1;
        } else if (expense.status === 'pending') {
          summary.pending.total += value;
          summary.pending.count += 1;
        } else if (expense.status === 'paid') {
          summary.paid.total += value;
          summary.paid.count += 1;
        }
      });

      return summary;
    },
    enabled: !!activeCompany?.id,
  });
}

export function useCreateExpense() {
  const queryClient = useQueryClient();
  const { activeCompany, user } = useAuth();

  return useMutation({
    mutationFn: async (expense: Omit<Expense, 'id' | 'company_id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by' | 'status'>) => {
      if (!activeCompany?.id || !user?.id) throw new Error('No active company or user');

      const { data, error } = await supabase
        .from('fleet_expenses')
        .insert([{ 
          ...expense, 
          company_id: activeCompany.id,
          created_by: user.id,
          updated_by: user.id,
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-summary'] });
      toast({ title: 'Despesa criada com sucesso' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao criar despesa',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateExpense() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Expense> & { id: string }) => {
      const { data, error } = await supabase
        .from('fleet_expenses')
        .update({ ...updates, updated_by: user?.id })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-summary'] });
      toast({ title: 'Despesa atualizada com sucesso' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao atualizar despesa',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('fleet_expenses')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-summary'] });
      toast({ title: 'Despesa excluída com sucesso' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao excluir despesa',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useMarkAsPaid() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, paidAt }: { id: string; paidAt: string }) => {
      const { data, error } = await supabase
        .from('fleet_expenses')
        .update({ paid_at: paidAt, updated_by: user?.id })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-summary'] });
      toast({ title: 'Despesa marcada como paga' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao marcar despesa como paga',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUploadAttachment() {
  const { activeCompany, user } = useAuth();

  return useMutation({
    mutationFn: async ({ file, expenseId }: { file: File; expenseId: string }) => {
      if (!activeCompany?.id || !user?.id) throw new Error('No active company or user');

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${activeCompany.id}/${expenseId}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('fleet-expenses')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('fleet-expenses')
        .getPublicUrl(fileName);

      return { url: publicUrl, name: file.name };
    },
    onError: (error) => {
      toast({
        title: 'Erro ao fazer upload',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
